<!--
  FrameX right panel — reduced frame properties (Free tier).

  Shows only Frame Scale, Mark, Width, Height and Flag for the selected
  frame(s), for both single and multi selection. Mark/Width/Height/Flag reuse
  ItemProperties in `framex` mode — a single record, or a `makeMultiItem`
  aggregate whose differing fields read "Mixed". Frame Scale is owned here
  because it reads the frames' *location* clips (with Mixed handling across the
  selection); ItemProperties' item-drawing clip scale can't span a
  multi-selection (its item.id becomes an array). Ported from
  LocationProperties' clip-scale logic, scoped to the selected frame locations.
-->
<script>
  import cloneDeep from "lodash/cloneDeep";

  import ItemProperties from "#lib/sidebar/ItemProperties.svelte";
  import SelectInput from "#lib/sidebar/SelectInput.svelte";
  import SidebarSection from "#lib/sidebar/SidebarSection.svelte";
  import CustomScaleModal from "#lib/sidebar/CustomScaleModal.svelte";
  import { scaleOptions, definedMultipliers } from "@local/extensions/utilities/scales.js";
  import { locRecord } from "#src/extensions/filter-items.js";
  import { currentTool } from "#src/stores/ui.js";
  import makeMultiItem from "#src/extensions/multi-item.js";
  import { MATCH_PAGE_SCALE, matchPageScaleOption } from "../match-page-scale.js";
  import { withClipScale } from "../clip-scale.js";
  import { overlayOversize } from "../oversize.js";
  import { plural } from "../labels.js";

  export let group;
  export let doc = null;
  export let disabled = false;
  /** The selected frame locations (Document.svelte's `selectedLocationList`). */
  export let locations = [];
  export let types = [];
  export let collections = [];
  export let customColumns = [];
  export let standardColumns = {};

  let customScaleModal;

  $: records = locations.map((l) => $group.items[l.record_id]).filter(Boolean);
  $: single = records.length === 1;
  // makeMultiItem returns null for < 2 items, so use the record directly when
  // one frame is selected and the aggregate otherwise.
  $: item = single ? records[0] : makeMultiItem($group, records, customColumns);
  $: isCollection = single ? Boolean(records[0]?.is_collection) : locations[0]?.record_type === "collection";
  $: hasClip = locations.some((l) => l.clip);

  // Frame Scale: the frames' clip drawing scale, "Mixed" when they disagree.
  $: clipScale = frameScaleOf(locations);
  // Overlays larger than their boundary — a wrong-scale telltale, surfaced
  // right above the field that fixes it. Informational only.
  $: oversizedCount = records.filter((r) => overlayOversize(r)).length;
  $: pages = doc?.data?.pages ?? {};
  // "Match page scale" leads the list when any selected frame differs from
  // its own page's scale; applying it is per-frame (each to its own page).
  $: matchOption = matchPageScaleOption(locations, pages);
  $: scaleValues = [...(matchOption ? [matchOption] : []), ...clipScaleOptions(clipScale)];

  function frameScaleOf(locs) {
    const first = locs[0]?.clip?.drawing_scale ?? null;
    for (const l of locs) {
      if ((l.clip?.drawing_scale ?? null) !== first) return "Mixed";
    }
    return first;
  }

  function clipScaleOptions(scale) {
    if (scale === "Mixed") {
      return [
        { label: "Mixed", value: "Mixed" },
        { label: "None", value: null },
        { label: "Set Custom Scale", value: "custom", unclickable: true },
        ...scaleOptions,
      ];
    } else if (scale && !definedMultipliers[scale]) {
      return [
        { label: "None", value: null },
        { label: "Set Custom Scale", value: "custom", unclickable: true },
        ...scaleOptions,
        { label: `Custom Scale: ${scale}`, value: scale },
      ];
    }
    return [
      { label: "None", value: null },
      { label: "Set Custom Scale", value: "custom", unclickable: true },
      ...scaleOptions,
    ];
  }

  function updateFrameScale(e) {
    const value = e.detail.value;
    if (value === "Mixed") return;
    const matching = value === MATCH_PAGE_SCALE;
    const seen = {};
    const updates = [];
    locations.forEach((loc) => {
      if (!loc.clip) return;
      // Matching resolves per-frame to that frame's own page scale; frames on
      // unscaled pages, or already matching, are left alone.
      const v = matching ? (pages[loc.page_id]?.scale ?? null) : value;
      if (matching && (!v || (loc.clip.drawing_scale ?? null) === v)) return;
      // Rescales drawing_offset with the scale so the drawing resizes about
      // the frame origin rather than the crop-box corner; the height lets an
      // untouched frame snap back to its stored registration point.
      const record = locRecord(loc, $group);
      const clip = withClipScale(loc.clip, v, {
        heightInches: record?.cache?.height_in,
      });
      updates.push({ type: "location", id: loc.id, diff: { clip } });
      if (record?.drawing?.attachment_id === loc.id && !seen[record.id]) {
        seen[record.id] = true;
        const drawing = cloneDeep(record.drawing);
        drawing.clip = clip;
        updates.push({
          type: loc.record_type === "collection" ? "item" : loc.record_type,
          id: record.id,
          diff: { drawing },
        });
      }
    });
    if (updates.length) group.update(updates);
  }

  function handleUpdateItem(e) {
    const { id, prop, value, diff } = e.detail;
    if (single) {
      group.updateItem(id, prop, value, diff);
    } else {
      group.updateMultipleItems(
        locations.map((l) => l.record_id),
        prop,
        value,
        diff,
      );
    }
  }
</script>

{#if item}
  <div class="fx-frame-props">
    <SidebarSection>
      <h3 class="px-2 mb-2">Properties</h3>
    </SidebarSection>
    <ItemProperties
      framex
      multiple={!single}
      {disabled}
      {group}
      {item}
      itemid={single ? item.id : null}
      {types}
      {collections}
      {customColumns}
      {standardColumns}
      {isCollection}
      settings={$group.data.settings}
      on:updateItem={handleUpdateItem} />
    {#if hasClip}
      <SidebarSection>
        <!-- Same remedy button as step 3's strip. FrameProperties sits in the
             right-panel tree where step 3's dispatch-up-through-FrameXPanel
             path doesn't exist — but that path only ever sets $currentTool,
             so this writes the store directly (Step1Scale's forced-placement
             re-arm is the in-tree precedent). The scale panel pops its
             waiting state off the tool change by itself. -->
        {#if oversizedCount > 0}
          <div class="fx-warning fx-oversize-note">
            <span class="fx-oversize-title">Check the frame scale.</span>
            <!-- One sentence pattern with step 3's strip; the one word of
                 difference is real — step 3 counts the working set, this
                 counts the selection. -->
            <span>
              {oversizedCount} selected frame{plural(oversizedCount)}
              {oversizedCount === 1 ? "is" : "are"} larger than
              {oversizedCount === 1 ? "its" : "their"} boundary.
            </span>
            <!-- Honors the panel's read-only state like the Frame Scale
                 field below it. (No render-harness exists for a unit test
                 of this — the suite is node --test pure modules only.) -->
            <button
              type="button"
              {disabled}
              class="fx-secondary-button fx-oversize-action"
              on:click={() => ($currentTool = "pick-scale-dim")}>
              Set scale from dimension
            </button>
          </div>
        {/if}
        <SelectInput
          {disabled}
          label="Frame Scale"
          labelWidth="7.5rem"
          value={clipScale}
          options={scaleValues}
          on:input={updateFrameScale}
          on:option-click={() => customScaleModal.open(clipScale)} />
      </SidebarSection>
    {/if}
  </div>
{/if}

<CustomScaleModal bind:this={customScaleModal} on:setScale={updateFrameScale} />

<style lang="scss">
  // Set the Properties section off from the frames list above it: a full-width
  // rule directly abutting the list, then space before the "Properties"
  // heading. No space above the rule.
  .fx-frame-props {
    padding-top: 10px;
    border-top: 1px solid var(--fx-border-light, #eceef1);
  }

  // Breathing room between the warning and the Frame Scale field it sits
  // above; the box itself is the shared .fx-warning, the two-row structure
  // mirrors step 2's lead-line pattern (weight separates the rows).
  .fx-oversize-note {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 6px;
  }

  .fx-oversize-title {
    font-weight: 600;
  }

  // Shrink-wrap the remedy button inside the strip — the shared
  // .fx-secondary-button is width: 100% and stays untouched.
  .fx-oversize-action {
    width: auto;
    align-self: flex-start;
    margin-top: 2px;
  }
</style>
